import type { ContactMessageResponse, ContactMessagesListResponse, ContactMessageStatus } from '@/types';

import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { apiClient } from '@/utils/api';
import { buildApiParams } from '@/utils/helpers';
import { createPaginationParams, createTableQueryOptions } from '@/components/table';

const CONTACT_MESSAGES_BASE = '/contact-messages';

export type ContactMessagesListParams = {
  page: number;
  per_page: number;
  filters: Record<string, string | undefined>;
  sort?: string;
  include?: string;
  refresh?: number;
};

export const contactMessagesListParamsAtom = atom<ContactMessagesListParams>({
  ...createPaginationParams(),
  filters: {}
});

export const contactMessagesHydratedAtom = atom<boolean>(false);

export const contactMessagesQueryAtom = atomWithQuery<ContactMessagesListResponse>((get) => {
  const params = get(contactMessagesListParamsAtom);
  const hydrated = get(contactMessagesHydratedAtom);

  return createTableQueryOptions({
    queryKey: ['contact-messages', params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        {
          page: params.page,
          per_page: params.per_page,
          sort: params.sort,
          include: params.include
        },
        params.filters || {}
      );

      const { data } = await apiClient.get(CONTACT_MESSAGES_BASE, { params: apiParams });
      return data as ContactMessagesListResponse;
    },
    enabled: hydrated
  });
});

export const contactMessageDetailIdAtom = atom<number | null>(null);
export const contactMessageDetailIncludeAtom = atom<string>('section,replies,notes,author,user');
export const contactMessageDetailRefreshAtom = atom<number>(0);

export const contactMessageDetailQueryAtom = atomWithQuery<ContactMessageResponse>((get) => {
  const messageId = get(contactMessageDetailIdAtom);
  const include = get(contactMessageDetailIncludeAtom);
  const refresh = get(contactMessageDetailRefreshAtom);

  return createTableQueryOptions({
    queryKey: ['contact-message-detail', messageId, include, refresh] as const,
    queryFn: async () => {
      if (!messageId) {
        throw new Error('Contact message id is required');
      }

      const params = include ? { include } : undefined;
      const { data } = await apiClient.get(`${CONTACT_MESSAGES_BASE}/${messageId}`, { params });
      return data as ContactMessageResponse;
    },
    enabled: Boolean(messageId)
  });
});

const touchContactMessagesList = (get: any, set: any) => {
  const currentParams = get(contactMessagesListParamsAtom);
  set(contactMessagesListParamsAtom, { ...currentParams, refresh: Date.now() });
};

const touchContactMessageDetail = (get: any, set: any, messageId?: number) => {
  const activeDetailId = get(contactMessageDetailIdAtom);
  if (messageId && activeDetailId && activeDetailId !== messageId) {
    return;
  }
  set(contactMessageDetailRefreshAtom, Date.now());
};

export const replyToContactMessage = atom(
  null,
  async (
    get,
    set,
    { messageId, message, isNote }: { messageId: number; message: string; isNote?: boolean }
  ) => {
    await apiClient.post(`${CONTACT_MESSAGES_BASE}/${messageId}`, {
      message,
      is_note: Boolean(isNote)
    });

    touchContactMessagesList(get, set);
    touchContactMessageDetail(get, set, messageId);
  }
);

export const updateContactMessage = atom(
  null,
  async (
    get,
    set,
    {
      messageId,
      message,
      isNote,
      parentMessageId
    }: { messageId: number; message: string; isNote?: boolean; parentMessageId?: number }
  ) => {
    await apiClient.put(`${CONTACT_MESSAGES_BASE}/${messageId}`, {
      message,
      is_note: isNote ?? undefined
    });

    touchContactMessagesList(get, set);
    touchContactMessageDetail(get, set, parentMessageId ?? messageId);
  }
);

export const changeContactMessageStatus = atom(
  null,
  async (get, set, { messageId, status }: { messageId: number; status: ContactMessageStatus }) => {
    await apiClient.put(`${CONTACT_MESSAGES_BASE}/${messageId}/status`, {
      status
    });

    touchContactMessagesList(get, set);
    touchContactMessageDetail(get, set, messageId);
  }
);

export const deleteContactMessage = atom(
  null,
  async (get, set, { messageId }: { messageId: number }) => {
    await apiClient.delete(`${CONTACT_MESSAGES_BASE}/${messageId}`);

    touchContactMessagesList(get, set);
    // Clear detail if it's the deleted message
    const activeDetailId = get(contactMessageDetailIdAtom);
    if (activeDetailId === messageId) {
      set(contactMessageDetailIdAtom, null);
    }
  }
);

